import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { KTIcon } from "../../../../_metronic/helpers";

interface FileUploadProps {
	label: string;
	value?: File | string | null;
	onChange: (file: File | string | null) => void;
	onUpload?: (file: File) => Promise<any>;
	className?: string;
	isInvalid?: boolean;
	required?: boolean;
	maxSizeMB?: number;
	accept?: string;
	description?: string;
	compact?: boolean;
	disabled?: boolean;
	hideDownload?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
	label,
	value,
	onChange,
	onUpload,
	className = "",
	isInvalid = false,
	required = false,
	maxSizeMB = 5,
	accept = ".pdf,.jpg,.jpeg,.png,image/*,application/pdf",
	description = "",
	compact = false,
	disabled = false,
	hideDownload = false,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);

	const handleFile = async (file: File) => {
		const fileSizeMB = file.size / (1024 * 1024);
		if (fileSizeMB > maxSizeMB + 0.5) {
			toast.error(`File size exceeds ${maxSizeMB}MB limit.`);
			return;
		}

		if (onUpload) {
			setIsUploading(true);
			try {
				const response = await onUpload(file);
				if (response.data?.status === 1) {
					// Prefer filename (relative path) over path (absolute internal URL)
					const uploadedPath =
						response.data?.data?.filename || response.data?.data?.path;
					if (uploadedPath) {
						onChange(uploadedPath);
						toast.success(
							response.data?.message || `${label} uploaded successfully`,
						);
					} else {
						toast.error(
							`Received success status but no file path for ${label}`,
						);
					}
				} else {
					toast.error(response.data?.message || `Failed to upload ${label}`);
				}
			} catch (error: any) {
				console.error(`Failed to upload ${label}`, error);
				const errorMessage =
					error.response?.data?.message || `Failed to upload ${label}`;
				toast.error(errorMessage);
			} finally {
				setIsUploading(false);
			}
		} else {
			onChange(file);
		}
	};

	const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) {
			handleFile(files[0]);
		}
	};

	const triggerUpload = () => {
		if (disabled) return;
		fileInputRef.current?.click();
	};

	const removeFile = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const isUploaded = typeof value === "string" && value.length > 0;
	const fileName =
		value instanceof File
			? value.name
			: typeof value === "string"
				? value.split("/").pop()
				: "";

	const getAbsoluteUrl = (path: string) => {
		if (!path) return "";
		const baseUrl =
			import.meta.env.VITE_APP_API_BASE_URL ||
			import.meta.env.VITE_APP_API_URL?.replace(/\/api\/?$/, "") ||
			"";
		const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

		if (path.startsWith("http")) {
			// Replace internal hostname with VITE_APP_API_BASE_URL
			try {
				const urlObj = new URL(path);
				const baseObj = new URL(baseUrl);
				urlObj.protocol = baseObj.protocol;
				urlObj.host = baseObj.host;
				return urlObj.toString();
			} catch (e) {
				return path;
			}
		}
		// Handle relative paths (./uploads... or uploads...)
		const cleanPath = path.replace(/^(\.\/|\/)/, "");
		return `${normalizedBase}${cleanPath}`;
	};

	// Browsers can render PDFs/images natively, but have no built-in viewer
	// for Office formats — opening those directly just downloads them. Route
	// those through Microsoft's Office Online Viewer instead, which renders
	// them in the new tab. This only works once the file URL is reachable
	// from the public internet (Office's servers have to fetch it), so on a
	// local dev/localhost URL it'll still just download.
	const OFFICE_VIEWER_EXTENSIONS = [
		".doc",
		".docx",
		".xls",
		".xlsx",
		".ppt",
		".pptx",
	];

	// Office's viewer can only ever fetch a publicly reachable URL — on a
	// local/private host it can never work, so skip straight to a direct
	// open there instead of sending the user to a dead-end error page.
	const isPubliclyReachable = (url: string) => {
		try {
			const hostname = new URL(url).hostname;
			return (
				hostname !== "localhost" &&
				hostname !== "127.0.0.1" &&
				!hostname.startsWith("192.168.") &&
				!hostname.startsWith("10.") &&
				!/^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
			);
		} catch {
			return false;
		}
	};

	const viewFile = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (typeof value !== "string") return;
		const url = getAbsoluteUrl(value);
		const ext = url.toLowerCase().slice(url.toLowerCase().lastIndexOf("."));
		if (OFFICE_VIEWER_EXTENSIONS.includes(ext) && isPubliclyReachable(url)) {
			window.open(
				`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`,
				"_blank",
			);
		} else {
			window.open(url, "_blank");
		}
	};

	const downloadFile = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (typeof value === "string") {
			const url = getAbsoluteUrl(value);
			const link = document.createElement("a");
			link.href = url;
			link.target = "_blank";
			link.setAttribute("download", fileName || "download");
			document.body.appendChild(link);
			link.click();
			link.remove();
		}
	};

	return (
		<div className={`fv-row${className}`}>
			{label && (
				<label
					className={`form-label ${required ? "required" : ""} fw-bold fs-6 mb-2`}
				>
					{label}
				</label>
			)}

			<div
				className={`d-flex align-items-center premium-file-upload overflow-hidden ${isInvalid ? "is-invalid border-danger" : ""} ${disabled ? "bg-light-secondary cursor-not-allowed" : "cursor-pointer"}`}
				onClick={triggerUpload}
				style={{
					border: isInvalid ? "1.5px solid #f1416c" : "1px solid #e2e8f0",
				}}
			>
				<div
					className="d-flex align-items-center justify-content-center px-4 py-3 h-100 border-end"
					style={{
						backgroundColor: disabled ? "#fcfcfd" : "transparent",
						backgroundImage: disabled
							? "none"
							: "linear-gradient(135deg, rgba(13, 22, 66, 0.06) 0%, rgba(26, 35, 126, 0.08) 100%)",
						minWidth: "140px",
						transition: "all 0.3s ease",
					}}
				>
					<KTIcon iconName="file-up" className="fs-3 text-primary me-2" />
					<span className="text-primary fw-bold fs-7">Choose File</span>
				</div>

				<div
					className="flex-grow-1 px-4 py-2 d-flex align-items-center h-100"
					style={{ backgroundColor: disabled ? "#fcfcfd" : "#ffffff" }}
				>
					{isUploading ? (
						<div className="d-flex align-items-center text-gray-400">
							<span className="spinner-border spinner-border-sm text-primary me-2"></span>
							<span>Uploading...</span>
						</div>
					) : isUploaded ? (
						<div className="d-flex align-items-center text-success fw-bold overflow-hidden">
							<KTIcon
								iconName="check-circle"
								className="fs-2 text-success me-2"
							/>
							<span className="text-truncate">
								{fileName || "File uploaded"}
							</span>
						</div>
					) : (
						<div className="text-gray-400 fw-semibold">
							No {label ? `${label} ` : ""}uploaded
						</div>
					)}
				</div>

				<input
					type="file"
					ref={fileInputRef}
					onChange={onFileSelect}
					accept={accept}
					style={{ display: "none" }}
				/>
			</div>

			{isUploaded && (
				<div className="d-flex gap-2 mt-3">
					<button
						type="button"
						className="btn btn-sm btn-primary d-flex align-items-center gap-2"
						onClick={viewFile}
						style={{ backgroundColor: "#5d53b4" }}
					>
						<KTIcon iconName="eye" className="fs-4 text-white" /> View
					</button>
					{!hideDownload && (
						<button
							type="button"
							className="btn btn-sm btn-primary d-flex align-items-center gap-2"
							onClick={downloadFile}
							style={{ backgroundColor: "#5d53b4" }}
						>
							<KTIcon iconName="download" className="fs-4 text-white" /> Download
						</button>
					)}
				</div>
			)}
			<div className="text-gray-400 fs-7 mt-1">
				{description || `Max ${maxSizeMB}MB (PDF, JPG, PNG)`}
			</div>
		</div>
	);
};

export { FileUpload };
